import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getTelegramWebApp, setTelegramTheme, triggerHaptic } from '@/lib/telegram';
import type { TelegramWebApp } from '@/types';

interface TelegramContextValue {
  webApp: TelegramWebApp | null;
  isReady: boolean;
  showMainButton: (text: string, onClick: () => void) => void;
  hideMainButton: () => void;
  showBackButton: (onClick: () => void) => void;
  hideBackButton: () => void;
  haptic: (
    type: 'impact' | 'notification' | 'selection',
    style?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' | 'error' | 'success' | 'warning'
  ) => void;
}

const TelegramContext = createContext<TelegramContextValue | null>(null);

export function TelegramProvider({ children }: { children: React.ReactNode }) {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const app = getTelegramWebApp();
    if (app) {
      app.ready();
      app.expand();

      // Force expansion on viewport change
      app.onEvent('viewportChanged', () => {
        if (!app.isExpanded) {
          app.expand();
        }
      });

      app.enableClosingConfirmation();
      setTelegramTheme();
      setWebApp(app);
      setIsReady(true);

      // Ensure expanded after a short delay
      setTimeout(() => {
        if (!app.isExpanded) {
          app.expand();
        }
      }, 100);
    }
  }, []);

  const showMainButton = useCallback(
    (text: string, onClick: () => void) => {
      if (webApp) {
        webApp.MainButton.setText(text);
        webApp.MainButton.onClick(onClick);
        webApp.MainButton.show();
      }
    },
    [webApp]
  );

  const hideMainButton = useCallback(() => {
    if (webApp) {
      webApp.MainButton.hide();
    }
  }, [webApp]);

  const showBackButton = useCallback(
    (onClick: () => void) => {
      if (webApp) {
        webApp.BackButton.onClick(onClick);
        webApp.BackButton.show();
      }
    },
    [webApp]
  );

  const hideBackButton = useCallback(() => {
    if (webApp) {
      webApp.BackButton.hide();
    }
  }, [webApp]);

  const haptic = useCallback(
    (
      type: 'impact' | 'notification' | 'selection',
      style?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' | 'error' | 'success' | 'warning'
    ) => {
      triggerHaptic(type, style);
    },
    []
  );

  return (
    <TelegramContext.Provider
      value={{
        webApp,
        isReady,
        showMainButton,
        hideMainButton,
        showBackButton,
        hideBackButton,
        haptic,
      }}
    >
      {children}
    </TelegramContext.Provider>
  );
}

export function useTelegram() {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within TelegramProvider');
  }
  return context;
}
